import { useEffect, useRef, useState } from "react";
import { Button, Input, Pagination, Select, View, ViewHeader } from "@/components";
import {
  File02Icon,
  FolderIcon,
  Image01Icon,
  SearchSmIcon,
  Upload01Icon,
  VideoRecorderIcon,
  VolumeMaxIcon,
} from "@vetty/icons";
import { handleAxiosError } from "@/helpers/axios";
import { useAssetsQuery } from "../../_hooks/useAssetsQuery";
import { useDeleteAssetMutation } from "../../_hooks/useDeleteAssetMutation";
import { useDuplicateAssetMutation } from "../../_hooks/useDuplicateAssetMutation";
import { useUploadAssetMutation } from "../../_hooks/useUploadAssetMutation";
import type { ApiAsset } from "../../_types/assets.types";
import { DeleteAssetModal } from "../delete-asset-modal";
import { AssetsList } from "../assets-list";
import styles from "./AssetsView.module.scss";

const KIND_OPTIONS = [
  { value: "all", label: "All kinds" },
  { value: "image", label: "Images" },
  { value: "audio", label: "Audio" },
  { value: "video", label: "Video" },
  { value: "other", label: "Other" },
];

const KindFilterIcon = ({ kind }: { kind: string }) => {
  const s = { width: 20, height: 20 };
  switch (kind) {
    case "image":
      return <Image01Icon style={s} />;
    case "audio":
      return <VolumeMaxIcon style={s} />;
    case "video":
      return <VideoRecorderIcon style={s} />;
    case "other":
      return <File02Icon style={s} />;
    default:
      return <FolderIcon style={s} />;
  }
};

const formatBytes = (n: number) => {
  if (n < 1024) return `${n} B`;
  if (n < 1024 * 1024) return `${(n / 1024).toFixed(1)} KB`;
  return `${(n / (1024 * 1024)).toFixed(1)} MB`;
};

export const AssetsView = () => {
  const [kind, setKind] = useState("all");
  const [q, setQ] = useState("");
  const [qDebounced, setQDebounced] = useState("");
  const [page, setPage] = useState(1);
  const [limit, setLimit] = useState(20);
  const [error, setError] = useState<string | null>(null);
  const [assetPendingDelete, setAssetPendingDelete] = useState<ApiAsset | null>(null);
  const fileRef = useRef<HTMLInputElement>(null);

  useEffect(() => {
    const t = setTimeout(() => {
      setQDebounced(q.trim());
    }, 300);
    return () => clearTimeout(t);
  }, [q]);

  useEffect(() => {
    setPage(1);
  }, [kind, qDebounced]);

  const { data, isLoading, isFetching, isError, refetch } = useAssetsQuery({
    page,
    limit,
    kind: kind === "all" ? undefined : kind,
    q: qDebounced || undefined,
  });

  const upload = useUploadAssetMutation();
  const del = useDeleteAssetMutation();
  const duplicate = useDuplicateAssetMutation();

  const items = data?.items ?? [];
  const meta = data?.meta ?? {
    total: 0,
    page,
    limit,
    totalPages: 1,
  };

  const onPickFile = () => fileRef.current?.click();

  const onFileChange = async (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    e.target.value = "";
    if (!file) return;
    setError(null);
    try {
      await upload.mutateAsync({ file, name: file.name });
    } catch (err) {
      setError(handleAxiosError(err)?.message ?? "Upload failed.");
    }
  };

  const onDuplicateAsset = (asset: ApiAsset) => {
    void duplicate.mutateAsync(asset).catch(() => {});
  };

  const confirmDeleteAsset = () => {
    if (!assetPendingDelete) return;
    void del
      .mutateAsync(assetPendingDelete.id)
      .then(() => setAssetPendingDelete(null))
      .catch(() => {});
  };

  const actionsPending = del.isPending || duplicate.isPending;

  return (
    <View>
      <div className={styles["comp__content"]}>
        <ViewHeader
          heading="Assets"
          description="Upload wallpapers, logos, and sounds that you can reuse in device branding.<br />Files are stored securely and referenced by branding packages."
        />

        <div className={styles["comp__toolbar"]}>
          <input ref={fileRef} type="file" hidden onChange={onFileChange} />
          <div className={styles["comp__left"]}>
            <div className={styles["comp__filters"]}>
              <div className={styles["comp__search"]}>
                <Input
                  id="assets-search"
                  value={q}
                  placeholder="Search…"
                  variant="secondary"
                  size="medium"
                  startContent={<SearchSmIcon style={{ width: 20, height: 20 }} />}
                  onChange={(v) => setQ(v)}
                />
              </div>
              <Select
                size="medium"
                variant="secondary"
                value={kind}
                startContent={<KindFilterIcon kind={kind} />}
                onChange={(v) => setKind(String(v))}
              >
                {KIND_OPTIONS.map((o) => (
                  <option key={o.value} value={o.value}>
                    {o.label}
                  </option>
                ))}
              </Select>
            </div>
          </div>

          <div className={styles["comp__actions"]}>
            <Button
              type="button"
              variant="primary"
              size="medium"
              onClick={onPickFile}
              disabled={upload.isPending}
              startContent={<Upload01Icon style={{ width: 20, height: 20 }} />}
            >
              {upload.isPending ? "Uploading…" : "Upload file"}
            </Button>
          </div>
        </div>

        {error ? <div className={styles["comp__error"]}>{error}</div> : null}

        <AssetsList
          items={items}
          formatBytes={formatBytes}
          isLoading={isLoading}
          isFetching={isFetching}
          isError={isError}
          onRetry={() => refetch()}
          onDuplicate={onDuplicateAsset}
          onRequestDelete={(asset) => setAssetPendingDelete(asset)}
          actionsDisabled={actionsPending}
        />

        <DeleteAssetModal
          isOpen={assetPendingDelete !== null}
          fileName={assetPendingDelete?.name ?? ""}
          onClose={() => setAssetPendingDelete(null)}
          onConfirm={confirmDeleteAsset}
          isDeleting={del.isPending}
        />

        {meta.total > 0 ? (
          <Pagination
            page={meta.page}
            limit={meta.limit}
            total={meta.total}
            totalPages={meta.totalPages}
            onPageChange={setPage}
            onRowsPerPageChange={(next) => {
              setLimit(next);
              setPage(1);
            }}
          />
        ) : null}
      </div>
    </View>
  );
};
